import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { format, parseISO } from 'date-fns';
import { da } from 'date-fns/locale';
import KPICard from '../../components/common/KPICard';
import DateRangePicker, { getDefaultDateRange, formatDateRange } from '../../components/common/DateRangePicker';
import ExportButton from '../../components/common/ExportButton';
import { useTrailer, useTrailerEconomics, useTrailerCargos, useCosts, useCreateCost, useDeleteCost, useTrailerHourEfficiency } from '../../hooks/useApi';
import type { DateRange, ManualCost } from '../../types';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('da-DK', {
    style: 'currency',
    currency: 'DKK',
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value: number, decimals = 0) => {
  return new Intl.NumberFormat('da-DK', {
    maximumFractionDigits: decimals,
  }).format(value);
};

export default function TrailerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [newCost, setNewCost] = useState({
    amount: '',
    description: '',
    category: 'OTHER' as ManualCost['category'],
    date: format(new Date(), 'yyyy-MM-dd'),
  });
  const { from, to } = formatDateRange(dateRange);

  const { data: trailer, isLoading: isLoadingTrailer, error: trailerError } = useTrailer(id || '');
  const { data: economicsData, isLoading: isLoadingEconomics } = useTrailerEconomics(from, to);
  const { data: cargos, isLoading: isLoadingCargos } = useTrailerCargos(id || '', from, to);
  const { data: costs } = useCosts({ trailerId: id, from, to });
  const createCost = useCreateCost();
  const deleteCost = useDeleteCost();

  const economics = economicsData?.trailers?.find(t => t.trailerId === id);
  const { data: hourEfficiencyData } = useTrailerHourEfficiency(from, to);
  const hourEfficiency = hourEfficiencyData?.trailers?.find(t => t.trailerId === id);

  const handleAddCost = async () => {
    if (!id || !newCost.amount) return;
    await createCost.mutateAsync({
      trailerId: id,
      amount: parseFloat(newCost.amount),
      description: newCost.description,
      category: newCost.category,
      date: newCost.date,
    });
    setCostDialogOpen(false);
    setNewCost({ amount: '', description: '', category: 'OTHER', date: format(new Date(), 'yyyy-MM-dd') });
  };

  const handleDeleteCost = async (costId: string) => {
    await deleteCost.mutateAsync(costId);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'd. MMM yyyy', { locale: da });
    } catch {
      return dateStr;
    }
  };

  if (trailerError) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/trailers')} sx={{ mb: 2 }}>
          Tilbage
        </Button>
        <Alert severity="error">Køretøj ikke fundet.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Tilbage
      </Button>

      {isLoadingTrailer ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : trailer ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" fontWeight={700}>
                  {trailer.number}
                </Typography>
                <Chip label={trailer.type} size="small" color="primary" variant="outlined" />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {trailer.alias || 'Ingen alias'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <DateRangePicker value={dateRange} onChange={setDateRange} />
              <ExportButton from={from} to={to} trailerId={id} />
            </Box>
          </Box>

          {/* Economics KPIs */}
          {isLoadingEconomics ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : economics ? (
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={4} md={2}>
                <KPICard title="Omsætning" value={formatCurrency(economics.revenue)} color="primary" />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <KPICard
                  title="Omkostninger"
                  value={formatCurrency(economics.totalCosts)}
                  subtitle={`Manuel: ${formatCurrency(economics.manualCosts)}`}
                  color="warning"
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <KPICard
                  title="Profit"
                  value={formatCurrency(economics.profit)}
                  color={economics.profit >= 0 ? 'success' : 'error'}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <KPICard title="Kilometer" value={`${formatNumber(economics.kilometers)} km`} color="info" />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <KPICard title="Kr/km" value={`${formatNumber(economics.revenuePerKm, 2)} kr`} color="info" />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <KPICard title="Ordrer" value={economics.cargoCount} subtitle={`${economics.routeCount} ruter`} color="success" />
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>Ingen data for perioden.</Alert>
          )}

          {/* Hour efficiency widget */}
          {hourEfficiency && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Timer & udfakturering
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <KPICard
                      title="Effektive timer"
                      value={`${formatNumber(hourEfficiency.effectiveHours, 1)} t`}
                      subtitle="fra DORA tachograf"
                      color="info"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KPICard
                      title="Udfakturerede timer"
                      value={`${formatNumber(hourEfficiency.invoicedHours, 1)} t`}
                      subtitle={`gns. ${formatCurrency(hourEfficiency.avgHourRate)}/t`}
                      color="success"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KPICard
                      title="Udfakt. grad"
                      value={`${formatNumber(hourEfficiency.utilizationPct, 1)}%`}
                      color={hourEfficiency.utilizationPct >= 85 ? 'success' : hourEfficiency.utilizationPct >= 70 ? 'warning' : 'error'}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KPICard
                      title="Tabt fakturering"
                      value={formatCurrency(hourEfficiency.lostRevenue)}
                      subtitle={`${formatNumber(Math.max(0, hourEfficiency.effectiveHours - hourEfficiency.invoicedHours), 1)} t ikke faktureret`}
                      color={hourEfficiency.lostRevenue > 0 ? 'error' : 'success'}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Two column layout: Cargos and Costs */}
          <Grid container spacing={2}>
            {/* Cargos/Orders */}
            <Grid item xs={12} lg={8}>
              <Card>
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Ordrer i perioden ({cargos?.length || 0})
                    </Typography>
                  </Box>
                  {isLoadingCargos ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : cargos && cargos.length > 0 ? (
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Dato</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Ref</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Kunde</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Fra → Til</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Omsætning</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cargos.map((cargo) => (
                            <TableRow key={cargo.id} hover>
                              <TableCell>{formatDate(cargo.destinationDate)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{cargo.ref}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                  {cargo.customerName || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                  {cargo.pickup?.city || '?'} → {cargo.delivery?.city || '?'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={500} color={cargo.revenue > 0 ? 'success.main' : 'text.secondary'}>
                                  {formatCurrency(cargo.revenue)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Ingen ordrer fundet i perioden
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Manual Costs */}
            <Grid item xs={12} lg={4}>
              <Card>
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Manuelle omkostninger
                    </Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => setCostDialogOpen(true)}>
                      Tilføj
                    </Button>
                  </Box>
                  {costs && costs.length > 0 ? (
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small">
                        <TableBody>
                          {costs.map((cost) => (
                            <TableRow key={cost.id} hover>
                              <TableCell>
                                <Typography variant="body2">{formatDate(cost.date)}</Typography>
                                <Typography variant="caption" color="text.secondary">{cost.description || cost.category}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={500}>{formatCurrency(cost.amount)}</Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ width: 40 }}>
                                <Tooltip title="Slet">
                                  <IconButton size="small" onClick={() => handleDeleteCost(cost.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Ingen manuelle omkostninger
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Add Cost Dialog */}
          <Dialog open={costDialogOpen} onClose={() => setCostDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Tilføj omkostning</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <TextField
                  label="Beløb (DKK)"
                  type="number"
                  value={newCost.amount}
                  onChange={(e) => setNewCost({ ...newCost, amount: e.target.value })}
                  fullWidth
                  autoFocus
                />
                <TextField
                  label="Beskrivelse"
                  value={newCost.description}
                  onChange={(e) => setNewCost({ ...newCost, description: e.target.value })}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Kategori</InputLabel>
                  <Select
                    value={newCost.category}
                    label="Kategori"
                    onChange={(e) => setNewCost({ ...newCost, category: e.target.value as ManualCost['category'] })}
                  >
                    <MenuItem value="FUEL">Brændstof</MenuItem>
                    <MenuItem value="MAINTENANCE">Vedligehold</MenuItem>
                    <MenuItem value="INSURANCE">Forsikring</MenuItem>
                    <MenuItem value="OTHER">Andet</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Dato"
                  type="date"
                  value={newCost.date}
                  onChange={(e) => setNewCost({ ...newCost, date: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCostDialogOpen(false)}>Annuller</Button>
              <Button variant="contained" onClick={handleAddCost} disabled={!newCost.amount || createCost.isPending}>
                Tilføj
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}
    </Box>
  );
}
